import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { useParams } from 'react-router-dom';
import { store } from '../../config/firebase';
import { useColorMode } from '../../hooks/colorModeProvider';
import { GameStatus } from '../../constants/gameConstants';
import { liveGameDocs } from '../../helpers/games';
import BSCircularProgress from '../ui/progress/circularProgress';

const normalizeName = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();

const getGroupList = (event) => {
  const groups = Array.isArray(event?.teamGroups) ? event.teamGroups : [];
  return groups.filter((group) => group && group.name).slice(0, 2);
};

const getGroupIdByName = (event, groupName) => {
  const groups = Array.isArray(event?.teamGroups) ? event.teamGroups : [];
  const targetKey = normalizeName(groupName);
  const match = groups.find((group) => normalizeName(group.name) === targetKey);
  return match ? match.id : null;
};

const getGroupColorByName = (event, groupName) => {
  const groups = Array.isArray(event?.teamGroups) ? event.teamGroups : [];
  const targetKey = normalizeName(groupName);
  const match = groups.find((group) => normalizeName(group.name) === targetKey);
  return match?.color || null;
};

const getGroupPoints = (event, games, groupAId, groupBId) => {
  let groupAScore = 0;
  let groupBScore = 0;
  const winPoints = Number(event?.gameFormat?.winPoints ?? 1) || 1;
  const drawPointsRaw = event?.gameFormat?.drawPoints;
  const drawPoints =
    drawPointsRaw != null ? Number(drawPointsRaw) : Number((winPoints / 2).toFixed(2));

  (games || []).forEach((game) => {
    if (!game.winningTeamId) {
      const teamAGroupId =
        (event.teams || []).find((team) => team.id === game.teams?.teamA?.id)?.groupId || null;
      const teamBGroupId =
        (event.teams || []).find((team) => team.id === game.teams?.teamB?.id)?.groupId || null;

      if (teamAGroupId === groupAId) {
        groupAScore += drawPoints;
      } else if (teamAGroupId === groupBId) {
        groupBScore += drawPoints;
      }

      if (teamBGroupId === groupAId) {
        groupAScore += drawPoints;
      } else if (teamBGroupId === groupBId) {
        groupBScore += drawPoints;
      }

      return;
    }

    const winningTeam = (event.teams || []).find((team) => team.id === game.winningTeamId);
    const groupId = winningTeam?.groupId || null;

    if (groupId === groupAId) {
      groupAScore += winPoints;
    } else if (groupId === groupBId) {
      groupBScore += winPoints;
    }
  });

  return { groupAScore, groupBScore };
};

const formatPoints = (value) => {
  const numericValue = Number(value || 0);
  if (Number.isInteger(numericValue)) {
    return String(numericValue);
  }
  return numericValue.toFixed(1);
};

const getHeadingBackground = (color, side) => {
  if (!color) {
    return 'transparent';
  }

  if (side === 'left') {
    return `linear-gradient(90deg, ${color} 0%, rgba(255, 255, 255, 0.1) 100%)`;
  }

  return `linear-gradient(270deg, ${color} 0%, rgba(255, 255, 255, 0.1) 100%)`;
};

const GroupMatchupTVOverlay = () => {
  const { eventId } = useParams();
  const { mode, setMode } = useColorMode();

  const [event, setEvent] = useState(null);
  const [linkedEvents, setLinkedEvents] = useState([]);
  const [eventGames, setEventGames] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setMode('light');
  }, [mode, setMode]);

  useEffect(() => {
    if (!eventId) {
      return;
    }

    setLoading(true);
    const docRef = doc(store, 'events', eventId);
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        setEvent({ id: docSnap.id, ...docSnap.data() });
      } else {
        setEvent(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [eventId]);

  useEffect(() => {
    if (!event) {
      setLinkedEvents([]);
      return;
    }

    const linkedIds = Array.isArray(event.linkedEvents) ? event.linkedEvents : [];
    if (linkedIds.length === 0) {
      setLinkedEvents([]);
      return;
    }

    let isMounted = true;
    const nextLinkedEvents = [];
    const unsubscribes = linkedIds.map((linkedId) => {
      const docRef = doc(store, 'events', linkedId);
      return onSnapshot(docRef, (docSnap) => {
        if (!isMounted) {
          return;
        }
        if (docSnap.exists()) {
          const existingIndex = nextLinkedEvents.findIndex((item) => item.id === linkedId);
          const nextEvent = { id: docSnap.id, ...docSnap.data() };
          if (existingIndex >= 0) {
            nextLinkedEvents[existingIndex] = nextEvent;
          } else {
            nextLinkedEvents.push(nextEvent);
          }
          setLinkedEvents([...nextLinkedEvents]);
        }
      });
    });

    return () => {
      isMounted = false;
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [event]);

  useEffect(() => {
    if (!event) {
      setEventGames({});
      return;
    }

    const eventsToWatch = [event, ...linkedEvents].filter(Boolean);
    const unsubscribes = eventsToWatch.map((evt) => {
      const collectionRef = collection(store, 'games');
      const q = query(
        collectionRef,
        where('event', '==', evt.id),
        where('status', '==', GameStatus.COMPLETE)
      );

      return onSnapshot(q, (querySnapshot) => {
        const games = liveGameDocs(querySnapshot).map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        setEventGames((prev) => ({ ...prev, [evt.id]: games }));
      });
    });

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [event, linkedEvents]);

  const canonicalGroups = useMemo(() => getGroupList(event), [event]);
  const eventsToRender = useMemo(
    () => [event, ...linkedEvents].filter(Boolean),
    [event, linkedEvents]
  );

  const totalPoints = useMemo(() => {
    if (!event || canonicalGroups.length < 2) {
      return { groupAScore: 0, groupBScore: 0 };
    }

    const [groupA, groupB] = canonicalGroups;
    return eventsToRender.reduce(
      (acc, evt) => {
        const groupAId = getGroupIdByName(evt, groupA.name);
        const groupBId = getGroupIdByName(evt, groupB.name);
        const points = getGroupPoints(evt, eventGames[evt.id], groupAId, groupBId);
        return {
          groupAScore: acc.groupAScore + points.groupAScore,
          groupBScore: acc.groupBScore + points.groupBScore
        };
      },
      { groupAScore: 0, groupBScore: 0 }
    );
  }, [canonicalGroups, event, eventGames, eventsToRender]);

  if (loading || !event) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'transparent',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
        <BSCircularProgress caption="Loading event..." size={60} />
      </Box>
    );
  }

  if (canonicalGroups.length < 2) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'transparent',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
        <Typography variant="h4" sx={{ color: '#fff' }}>
          Add two team groups to display this view.
        </Typography>
      </Box>
    );
  }

  const [groupA, groupB] = canonicalGroups;
  const groupAColor = getGroupColorByName(event, groupA.name);
  const groupBColor = getGroupColorByName(event, groupB.name);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        overflow: 'hidden',
        zIndex: 9999
      }}>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          px: 4,
          py: 4,
          overflowY: 'auto'
        }}>
        {/* Main Matchup Card */}
        <Box
          sx={{
            width: '100%',
            maxWidth: 1400,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            borderRadius: 3,
            p: 6,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)'
          }}>
          <Box display="grid" gridTemplateColumns="1fr auto 1fr" alignItems="center" gap={4}>
            {/* Group A */}
            <Box
              textAlign="right"
              sx={{
                background: getHeadingBackground(groupAColor, 'left'),
                borderRadius: 2,
                px: 4,
                py: 3
              }}>
              <Typography
                sx={{
                  fontWeight: 900,
                  color: '#fff',
                  fontSize: '4.5rem',
                  lineHeight: 1,
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)'
                }}>
                {groupA.name}
              </Typography>
            </Box>

            {/* Score */}
            <Box textAlign="center">
              <Typography
                sx={{
                  fontWeight: 900,
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '6rem',
                  lineHeight: 1
                }}>
                <Box component="span" sx={{ color: groupAColor || 'text.primary' }}>
                  {formatPoints(totalPoints.groupAScore)}
                </Box>
                <Box component="span" sx={{ color: '#ffffff', fontSize: '5rem' }}>
                  -
                </Box>
                <Box component="span" sx={{ color: groupBColor || 'text.primary' }}>
                  {formatPoints(totalPoints.groupBScore)}
                </Box>
              </Typography>
            </Box>

            {/* Group B */}
            <Box
              textAlign="left"
              sx={{
                background: getHeadingBackground(groupBColor, 'right'),
                borderRadius: 2,
                px: 4,
                py: 3
              }}>
              <Typography
                sx={{
                  fontWeight: 900,
                  color: '#fff',
                  fontSize: '4.5rem',
                  lineHeight: 1,
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)'
                }}>
                {groupB.name}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            width: '100%',
            maxWidth: 1400,
            mt: 2,
            display: 'grid',
            gap: 1.5
          }}>
          {eventsToRender.map((evt) => {
            const groupAId = getGroupIdByName(evt, groupA.name);
            const groupBId = getGroupIdByName(evt, groupB.name);
            const eventGroupAColor = getGroupColorByName(evt, groupA.name) || groupAColor;
            const eventGroupBColor = getGroupColorByName(evt, groupB.name) || groupBColor;
            const groupATeams = (evt.teams || []).filter((team) => team.groupId === groupAId);
            const groupBTeams = (evt.teams || []).filter((team) => team.groupId === groupBId);
            const points = getGroupPoints(evt, eventGames[evt.id], groupAId, groupBId);

            return (
              <Box
                key={evt.id}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: 'rgba(0, 0, 0, 0.9)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)'
                }}>
                <Box
                  display="grid"
                  gridTemplateColumns={{ xs: '1fr', md: '1fr auto 1fr' }}
                  alignItems="start"
                  gap={2}>
                  <Box textAlign={{ xs: 'center', md: 'left' }}>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        color: eventGroupAColor || 'text.primary',
                        fontSize: '2rem',
                        lineHeight: 1.1,
                        mb: 0.5
                      }}>
                      {formatPoints(points.groupAScore)}
                    </Typography>
                    {groupATeams.length > 0 ? (
                      groupATeams.map((team) => (
                        <Typography
                          key={team.id}
                          variant="body1"
                          sx={{ fontSize: '1.2rem', color: '#ffffff' }}>
                          {team.name}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2" sx={{ color: '#ffffff', opacity: 0.7 }}>
                        No teams in {groupA.name}
                      </Typography>
                    )}
                  </Box>

                  <Box textAlign="center">
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: '#ffffff',
                        fontSize: '1.4rem',
                        lineHeight: 1.2,
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.15)'
                      }}>
                      {evt.name}
                    </Typography>
                  </Box>

                  <Box textAlign={{ xs: 'center', md: 'right' }}>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        color: eventGroupBColor || 'text.primary',
                        fontSize: '2rem',
                        lineHeight: 1.1,
                        mb: 0.5
                      }}>
                      {formatPoints(points.groupBScore)}
                    </Typography>
                    {groupBTeams.length > 0 ? (
                      groupBTeams.map((team) => (
                        <Typography
                          key={team.id}
                          variant="body1"
                          sx={{ fontSize: '1.2rem', color: '#ffffff' }}>
                          {team.name}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2" sx={{ color: '#ffffff', opacity: 0.7 }}>
                        No teams in {groupB.name}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default GroupMatchupTVOverlay;
